import { UIThemeProvider } from '@all-manager/design-system-v2/theme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import { GlobalSnackbar } from './components/GlobalSnackbar/GlobalSnackbar';
import Layout from './components/Layout';
import { AuthProvider } from './contexts/AuthProvider';
import { SpaceProvider } from './contexts/SpaceProvider';
import AccountPage from './features/account/pages/AccountPage/AccountPage';
import AiChatPage from './features/aiChat/pages/AiChatPage/AiChatPage';
import { ForgotPasswordPage } from './features/authentication/pages/ForgotPasswordPage';
import Login from './features/authentication/pages/Login';
import { ResetPasswordPage } from './features/authentication/pages/ResetPasswordPage';
import Calendar from './features/calendar/pages/Calendar';
import DocumentsListPage from './features/documents/pages/DocumentsListPage/DocumentsListPage';
import InboxPage from './features/inbox/pages/InboxPage/InboxPage';
import GoogleDriveCallbackPage from './features/google-drive/pages/GoogleDriveCallbackPage/GoogleDriveCallbackPage';
import { MeetingWarning } from './features/meetings/components/MeetingWarning/MeetingWarning';
import MeetingsPage from './features/meetings/pages/MeetingsPage';
import PrivateNotes from './features/privateNotes/pages/PrivateNotes';
import ProviderPage from './features/providers/pages/ProviderPage';
import ProvidersListPage from './features/providers/pages/ProvidersListPage';
import { SpaceSelectionPage } from './features/spaces/pages/SpaceSelectionPage/SpaceSelectionPage';
import SpaceSettingsPage from './features/spaces/pages/SpaceSettingsPage/SpaceSettingsPage';
import PendingSubTopicPage from './features/topics/pages/PendingSubTopicPage/PendingSubTopicPage';
import SubTopicPage from './features/topics/pages/SubTopicPage';
import TopicListPage from './features/topics/pages/TopicListPage';
import { useAuth } from './hooks/useAuth';
import { useRouteTracking } from './hooks/useRouteTracking';
import { useSpace } from './hooks/useSpace';
import AccountingPage from './features/accounting/pages/AccountingPage';
import QuoteGroupPage from './features/quotes/pages/QuoteGroupPage';
import QuoteGroupsPage from './features/quotes/pages/QuoteGroupsPage';
import { usePermissions, type Permissions } from './hooks/usePermissions';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function SpaceRoute({ children }: { children: React.ReactNode }) {
  const { slug } = useParams<{ slug: string }>();
  const { memberships, switchSpace, isLoading } = useSpace();

  if (isLoading) {
    return null;
  }

  const match = memberships.find((m) => m.space.slug === slug);

  if (!match) {
    return <Navigate to="/spaces" replace />;
  }

  // Ensure the store reflects the current URL slug
  if (slug) {
    switchSpace(slug);
  }

  return <>{children}</>;
}

type PermissionRouteProps = {
  allow: (permissions: Permissions) => boolean;
  children: React.ReactNode;
};

function PermissionRoute(props: PermissionRouteProps) {
  const permissions = usePermissions();
  const { slug } = useParams<{ slug: string }>();

  if (permissions.isLoading) {
    return null;
  }

  if (!props.allow(permissions)) {
    return <Navigate to={`/spaces/${slug}`} replace />;
  }

  return <>{props.children}</>;
}

const canAdminSpace = (permissions: Permissions) => permissions.isAdmin;
const canSeePrivateNotes = (permissions: Permissions) => permissions.canSeePrivateNotes;
const canSeeInbox = (permissions: Permissions) => permissions.canSeeInbox;
const canUseAssistant = (permissions: Permissions) => permissions.canUseAssistant;

function SpaceRedirect() {
  const { memberships, isSuperAdmin, isLoading } = useSpace();

  if (isLoading) {
    return null;
  }

  // Super-admins or users with multiple spaces: show selection
  if (isSuperAdmin || memberships.length > 1) {
    return <Navigate to="/spaces" replace />;
  }

  // Single membership: redirect directly
  if (memberships.length === 1) {
    return <Navigate to={`/spaces/${memberships[0].space.slug}`} replace />;
  }

  // No memberships
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  useRouteTracking();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/oauth/google/callback"
        element={
          <PrivateRoute>
            <GoogleDriveCallbackPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/spaces"
        element={
          <PrivateRoute>
            <SpaceSelectionPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/spaces/:slug"
        element={
          <PrivateRoute>
            <SpaceRoute>
              <Layout />
            </SpaceRoute>
          </PrivateRoute>
        }
      >
        <Route index element={<PendingSubTopicPage />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="topics/:subTopicId" element={<SubTopicPage />} />
        <Route path="providers" element={<ProvidersListPage />} />
        <Route path="documents" element={<DocumentsListPage />} />
        <Route path="inbox" element={<PermissionRoute allow={canSeeInbox}><InboxPage /></PermissionRoute>} />
        <Route path="assistant" element={<PermissionRoute allow={canUseAssistant}><AiChatPage /></PermissionRoute>} />
        <Route path="assistant/:conversationId" element={<PermissionRoute allow={canUseAssistant}><AiChatPage /></PermissionRoute>} />
        <Route path="providers/:providerId/*" element={<ProviderPage />} />
        <Route path="topics-management" element={<TopicListPage />} />
        <Route path="topics-management/:topicId" element={<TopicListPage />} />
        <Route path="quotes" element={<QuoteGroupsPage />} />
        <Route path="quotes/:groupId" element={<QuoteGroupPage />} />
        <Route path="accounting" element={<AccountingPage />} />
        <Route path="council/meetings" element={<MeetingsPage />} />
        <Route path="private-notes" element={<PermissionRoute allow={canSeePrivateNotes}><PrivateNotes /></PermissionRoute>} />
        <Route path="account" element={<AccountPage />} />
        <Route path="settings" element={<PermissionRoute allow={canAdminSpace}><SpaceSettingsPage /></PermissionRoute>} />
        <Route path="settings/:section" element={<PermissionRoute allow={canAdminSpace}><SpaceSettingsPage /></PermissionRoute>} />
      </Route>
      <Route
        path="/"
        element={
          <PrivateRoute>
            <SpaceRedirect />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 30,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <UIThemeProvider>
        <Router>
          <AuthProvider>
            <SpaceProvider>
              <MeetingWarning />
              <AppRoutes />
            </SpaceProvider>
          </AuthProvider>
        </Router>
        <GlobalSnackbar />
      </UIThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
